import {API, APIError, APIResponseError} from "./base";
import {AdminAPI} from "./admin";
import {GenericResponse} from "./generic";
import {Sensor, SensorAPI, SensorExtended} from "./sensor";
import {SensorSet, SensorSetAPI, SensorSetsResponse} from "./set";

export type Reading = {
    value: number;
    date: string;
};

export type MaybeReading = Reading | null;

export type TableRow = {
    from: string;
    to: string;
    min: Reading;
    max: Reading;
    average: number;
    samples: number;
}

export type TableResponse = GenericResponse<{
    table: TableRow[];
}>;

export type User = {
    id?: number;
    name: string;
}

export type ChangelogResponse = {
    changelog: Changelog[]
}

export type ChangelogSection = {
    label: string;
    entries: string[];
};

export type Changelog = {
    version: string;
    sections: ChangelogSection[];
};

export type APIMeta = {
    version: string;
    title?: string | null;
}

export type CurrentReading = {
    average: number;
    diff: number;
} & Reading;

export type CurrentReadingResponse = GenericResponse<{
    reading: CurrentReading;
}>;

export type PastDaysResponse = GenericResponse<{
    readings: Reading[];
}>;

export type TableOpts = {
    granularity?: "every-minute" | "5m" | "hourly" | "daily" | "monthly" | "yearly" | "hour-of-day" | "month-of-year" | "total" | "month-yearly" | "day-yearly" | "day-monthly";
    from?: Date;
    to?: Date;
    interval?: string;
    order?: "date" | "min" | "max" | "average" | "samples";
    direction?: "ASC" | "DESC";
    limit?: number;
}

export class TemperatureAPI extends API {
    admin: AdminAPI = new AdminAPI(this);
    sensor: SensorAPI = new SensorAPI(this);
    sets: SensorSetAPI = new SensorSetAPI(this);

    async currentUser(): Promise<User | null> {
        try {
            return (await this.fetchJSON<User>("v3/users/me"));
        } catch (e) {
            if (e instanceof APIResponseError && e.response.status === 401) {
                return null;
            }
            throw e;
        }
    }

    async changelog(): Promise<Changelog[]> {
        return (await this.fetchJSON<ChangelogResponse>("/changelog.json")).changelog;
    }

    async meta(): Promise<APIMeta> {
        return await this.fetchJSON<APIMeta>("v3");
    }

    async current(sensor: Sensor): Promise<CurrentReading> {
        const url = new URL("current", this.base);
        url.searchParams.set("sensor", sensor.id.toString());

        return (await this.fetchJSON<CurrentReadingResponse>(url)).reading;
    }

    async pastDays(sensor: Sensor, days?: number): Promise<Reading[]> {
        const url = new URL("pastdays", this.base);
        url.searchParams.set("sensor", sensor.id.toString());

        if (days !== undefined) {
            url.searchParams.set("days", days.toString());
        }

        return (await this.fetchJSON<PastDaysResponse>(url)).readings;
    }

    async sensorInfo(sensor: Sensor): Promise<SensorExtended> {
        const url = new URL("info", this.base);
        url.searchParams.set("sensor", sensor.id.toString());

        return (await this.fetchJSON<GenericResponse<{sensor: SensorExtended}>>(url)).sensor;
    }

    async table(sensor: Sensor, opts: TableOpts): Promise<TableResponse> {
        const url = new URL("table", this.base);
        url.searchParams.set("sensor", sensor.id.toString());

        for (const key of (["granularity", "interval", "order", "direction", "limit"] as (keyof TableOpts)[])) {
            const val = opts[key];
            if (val !== undefined) {
                url.searchParams.set(key, val.toString());
            }
        }

        for (const key of ["from", "to"] as (keyof TableOpts)[]) {
            const val = opts[key];
            if (val !== undefined) {
                url.searchParams.set(key, (val as Date).toISOString())
            }
        }

        return (await this.fetchJSON<TableResponse>(url));
    }
}

export type WithAPI = {
    api: TemperatureAPI;
};
